import { type ReactElement, type ReactNode } from "react"
import { cn } from "cn"

import { selectIsXS } from "@/lib/storage/app-slice"
import { useAppSelector } from "@/lib/storage/hooks"
import {
  createDrawerHandle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  type DrawerContentProps,
  type DrawerHandle,
} from "./drawer"
import {
  createPopoverHandle,
  Popover,
  PopoverContent,
  PopoverTrigger,
  type PopoverContentProps,
  type PopoverHandle,
} from "./popover"

export type AdaptivePopoverHandle = {
  drawer: NonNullable<DrawerHandle>
  popover: NonNullable<PopoverHandle>
}

export function createAdaptivePopoverHandle(): AdaptivePopoverHandle {
  return {
    drawer: createDrawerHandle(),
    popover: createPopoverHandle(),
  }
}

type AdaptivePopoverTriggerProps = Omit<
  Parameters<typeof PopoverTrigger>[0],
  "handle"
> & {
  handle: AdaptivePopoverHandle
}

export function AdaptivePopoverTrigger({
  handle,
  openOnHover = true,
  delay = 30,
  closeDelay,
  ...props
}: AdaptivePopoverTriggerProps) {
  const isXS = useAppSelector(selectIsXS)
  if (isXS) {
    return <DrawerTrigger handle={handle.drawer} {...props} />
  }
  return (
    <PopoverTrigger
      handle={handle.popover}
      openOnHover={openOnHover}
      delay={delay}
      closeDelay={closeDelay}
      {...props}
    />
  )
}

export function AdaptivePopover({
  open,
  onOpenChange,
  popoverContentProps,
  drawerContentProps,
  separator = true,
  openOnHover = true,
  trigger,
  children,
  title,
  description,
  handle,
}: {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  trigger?: ReactElement
  children: ReactNode
  title: string | ReactNode
  description: string
  popoverContentProps?: PopoverContentProps
  drawerContentProps?: DrawerContentProps
  separator?: boolean
  openOnHover?: boolean
  handle?: AdaptivePopoverHandle
}) {
  const isXS = useAppSelector(selectIsXS)

  return isXS ? (
    <Drawer handle={handle?.drawer} open={open} onOpenChange={onOpenChange}>
      {trigger && <DrawerTrigger render={trigger} nativeButton={false} />}
      <DrawerContent {...drawerContentProps}>
        <DrawerHeader className={cn(separator && "border-b pb-4", "text-left")}>
          <DrawerTitle>{title}</DrawerTitle>
        </DrawerHeader>
        <DrawerDescription className="sr-only">{description}</DrawerDescription>
        <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto p-4">
          {children}
        </div>
      </DrawerContent>
    </Drawer>
  ) : (
    <Popover handle={handle?.popover} open={open} onOpenChange={onOpenChange}>
      {trigger && (
        <PopoverTrigger
          render={trigger}
          nativeButton={false}
          openOnHover={openOnHover}
        />
      )}
      <PopoverContent {...popoverContentProps}>{children}</PopoverContent>
    </Popover>
  )
}
